export interface OklabColor {
  L: number;
  a: number;
  b: number;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const toByte = (n: number) => Math.round(clamp01(n) * 255);

function linearToSrgbChannel(x: number): number {
  const v = clamp01(x);
  return v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

export function oklabToRgb({ L, a, b }: OklabColor): string {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  const r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  return `rgb(${toByte(linearToSrgbChannel(r))} ${toByte(linearToSrgbChannel(g))} ${toByte(linearToSrgbChannel(bl))})`;
}

export function oklchToOklab(L: number, C: number, hDegrees: number): OklabColor {
  const h = (hDegrees * Math.PI) / 180;
  return { L, a: C * Math.cos(h), b: C * Math.sin(h) };
}

export function oklabDistance(a: OklabColor, b: OklabColor): number {
  return Math.hypot(a.L - b.L, a.a - b.a, a.b - b.b);
}

export function makeColorPair(delta: number, random = Math.random): { reference: string; altered: string; meta: Record<string, number> } {
  const L = 0.66 + (random() - 0.5) * 0.08;
  const C = 0.12;
  const hue = random() * 360;
  const cappedDelta = Math.min(delta, 2 * C * 0.98);
  const hueOffsetRadians = 2 * Math.asin(cappedDelta / (2 * C));
  const direction = random() > 0.5 ? 1 : -1;
  const targetHue = hue + direction * (hueOffsetRadians * 180) / Math.PI;
  const referenceLab = oklchToOklab(L, C, hue);
  const alteredLab = oklchToOklab(L, C, targetHue);
  return {
    reference: oklabToRgb(referenceLab),
    altered: oklabToRgb(alteredLab),
    meta: {
      L,
      C,
      hue,
      targetHue,
      actualDelta: oklabDistance(referenceLab, alteredLab)
    }
  };
}

export function makeContrastPaint(delta: number): { base: string; signal: string; meta: Record<string, number> } {
  const base = 21;
  const signal = Math.round(base + Math.max(1, delta * 230));
  return {
    base: `rgb(${base} ${base + 1} ${base + 10})`,
    signal: `rgb(${signal} ${signal + 1} ${signal + 10})`,
    meta: { base, signal, quantizedContrast: (signal - base) / 255 }
  };
}

export function formatThreshold(modality: 'color' | 'contrast' | 'pitch', value: number): string {
  if (modality === 'pitch') return `${value.toFixed(value < 10 ? 1 : 0)} cents`;
  if (modality === 'contrast') return `${value.toFixed(value < 2 ? 2 : 1)}% contrast`;
  return `${value.toFixed(value < 2 ? 2 : 1)} ΔEOK×100`;
}
